import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Trans, useTranslation } from 'react-i18next';

import {
  AlertTitle,
  Box,
  createFilterOptions,
  Divider,
  FormHelperText,
  FormLabel,
  Grid,
  IconButton,
  Link,
  RadioGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import ValidatedDropdown, { DropdownOption } from 'components/Common/ValidatedDropdown';
import ValidatedTextField from 'components/Common/ValidatedTextField';
import { useFormikContext } from 'formik';
import { GlobalState } from 'reducers/stateTypes';
import ValidatedRadio from 'components/Common/ValidatedRadio';
import ContactNote, { AdditionalAttendee } from 'types/ContactNote';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { selectIsUsedInBr } from 'selectors/contactNoteListSelectors';
import { JBAlert, JBColors, JBIcon, JBSection } from '@juliusbaer-gcrm/gcrm-frontend-components';
import { openModal } from 'reducers/modalReducer';
import { DOCUMENT_COUNTRY_GUIDELINES, DOCUMENT_JBG_2038_00, DOCUMENT_JBG_2048_00 } from 'services/constants';
import { ContactType, ContactTypeOptions } from 'views/ContactNoteForm/components/data/contactTypeOptions';
import ContactNoteFormRelatedPartners from 'views/ContactNoteForm/components/ContactNoteFormRelatedPartners';
import CustomDateTimePicker from 'views/ContactNoteForm/components/CustomDateTimePicker';
import ContactNoteFormPartnerDropdown from 'views/ContactNoteForm/components/ContactNoteFormPartnerDropdown';
import topicOptions from 'views/ContactNoteForm/components/data/topicOptions';
import initiatedByOptions from 'views/ContactNoteForm/components/data/initiatedByOptions';
import DocumentDropZone from 'views/ContactNoteForm/components/DocumentUpload/DocumentDropZone';

/**
 * The form to create a new contact note.
 */
type Props = {
  title: string;
  isNoteFromEmail?: boolean;
  showExpandButton?: boolean;
};

export enum ContactDirection {
  INCOMING = 'INCOMING',
  OUTGOING = 'OUTGOING',
}

type EnumKeys<T> = keyof T;

export type ContactDirectionType = EnumKeys<typeof ContactDirection>;

const ContactnoteForm = ({ isNoteFromEmail = false, showExpandButton, title }: Props) => {
  const { values, setFieldValue, errors, touched, submitForm, isSubmitting, dirty, isValid, validateForm } =
    useFormikContext<ContactNote>();
  const countryOptions = useSelector((state: GlobalState) => state.resources.countries);
  const employeeOptions = useSelector((state: GlobalState) => state.resources.employeeOptions);

  const employeeOptionsWithInactiveUsers: DropdownOption[] = useMemo(() => {
    return employeeOptions.concat(
      values.additionalAttendees
        ?.map((user: AdditionalAttendee) => {
          return {
            label: user?.fullName + ' (' + user.userId + ')',
            value: user.userId,
            name: user?.fullName,
          };
        })
        .filter((x) => !employeeOptions.includes(x)),
    );
  }, [employeeOptions, values.additionalAttendees]);

  const isSourceTravelTool = values.source === 'MYTRAVEL';
  const contactTypesThatAllowLocation = new Set([ContactType.MEETING, ContactType.EVENT, ContactType.OTHER]);
  const contactTypesThatAllowDirection = [
    ContactType.EMAIL,
    ContactType.FAX,
    ContactType.LETTER,
    ContactType.PHONE_CALL,
  ];
  const [isExpanded, setIsExpanded] = useState(false);
  const { t } = useTranslation();
  const usedInBr = useSelector(selectIsUsedInBr);
  const dispatch = useDispatch();
  const responsibleRM = `${values.partner?.relationshipManager?.fullName} (${values.partner?.relationshipManager?.uid})`;

  const handleExpandClick = () => setIsExpanded(!isExpanded);

  useEffect(() => {
    window.addEventListener('message', handleMessage);

    async function handleMessage(event: any) {
      const message = event.data.message;
      if (message === 'changeTabSaveCheck') {
        if (dirty) {
          dispatch(
            openModal({
              modalType: 'ConfirmNavigationModal',
              modalProps: {
                submitForm,
                isSubmitting,
                validateForm,
                isValid,
              },
            }),
          );
        } else {
          window.parent.postMessage({ message: 'changeTab' }, '*');
        }
      }
    }

    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [dirty]);

  useEffect(() => {
    // Erase city and country values when the contact-type doesn't explicitly allow location info AND there's no cross-border-activity
    if (
      !contactTypesThatAllowLocation.has(values.contactType) &&
      (values.crossBorderActivity as unknown as string) === 'false'
    ) {
      setFieldValue('locationCountry', '');
      setFieldValue('locationCity', '');
    }
  }, [values.crossBorderActivity, values.contactType]);

  return (
    <JBSection
      title={title}
      disableCollapsible={!showExpandButton}
      avatar={<JBIcon iconName="contactNote" size="xl" />}
    >
      <Grid container rowSpacing={3} columnSpacing={2}>
        {isSourceTravelTool && (
          <Grid item xs={12} md={12}>
            <Divider textAlign="left">{t('noteForm.partnerReference', 'Partner reference')}</Divider>
          </Grid>
        )}
        {!isSourceTravelTool && (
          <>
            {/* For contact note from email */}
            {(isNoteFromEmail || usedInBr) && (
              <>
                <Grid item xs={12}>
                  <Divider textAlign="left">{t('noteForm.partnerReference', 'Partner reference')}</Divider>
                </Grid>
                <Grid item xs={12} md={6}>
                  <ContactNoteFormPartnerDropdown usedInBr={usedInBr} />
                </Grid>
              </>
            )}
          </>
        )}

        {isSourceTravelTool && (
          <>
            <Grid item xs={12} md={6}>
              <ValidatedTextField
                disabled={true}
                label={
                  isSourceTravelTool
                    ? t('noteForm.visitedPartner', 'Visited partner')
                    : t('noteForm.partner', 'Partner')
                }
                name="partner.fullName"
                maxChar={200}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <ValidatedTextField
                customValue={responsibleRM}
                disabled
                label={t('noteForm.responsibleRm', 'Responsible RM')}
                name={' '}
              />
            </Grid>
          </>
        )}
        <Grid item xs={12}>
          <Divider textAlign="left">{t('noteForm.contactDetails.attendees', 'Attendees')}</Divider>
        </Grid>

        <Grid item xs={12}>
          <ValidatedDropdown
            label={
              isSourceTravelTool
                ? t('noteForm.additionalTravelers', 'Additional travelers')
                : t('noteForm.attendeesBank', 'Attendees (bank)')
            }
            name="additionalAttendees"
            options={employeeOptions}
            isMultiChoice={true}
            disabled={isSourceTravelTool}
            filterOptions={createFilterOptions({
              matchFrom: 'any',
              limit: 50,
            })}
            onChange={async (_, value: DropdownOption[]) => {
              setFieldValue(
                'additionalAttendees',
                value
                  ? value.map((selectedValue) => {
                      const existingAttendee = values.additionalAttendees?.find(
                        (attendee) => attendee.userId === selectedValue.value,
                      );
                      return {
                        userId: selectedValue.value,
                        ...(existingAttendee ? { id: existingAttendee.id } : []),
                      };
                    })
                  : [],
              );
            }}
            value={values.additionalAttendees?.map((additionalAttendee) => {
              if (!employeeOptions) {
                return 'N/A';
              }
              return employeeOptionsWithInactiveUsers.find(
                (userOption) => userOption.value === additionalAttendee.userId,
              );
            })}
          />
        </Grid>

        {!usedInBr && (
          <Grid item xs={12}>
            <ContactNoteFormRelatedPartners />
          </Grid>
        )}

        <Grid item xs={12}>
          <Divider textAlign="left">{t('noteForm.contactDetails.title', 'Contact details')}</Divider>
        </Grid>
        <Grid item xs={12}>
          <ValidatedTextField
            customId="subject-text-input"
            label={t('noteForm.contactDetails.subject', 'Subject')}
            name="subject"
            maxChar={200}
            isMandatory
            showCharacterCount
          />
        </Grid>

        <Grid item xs={6}>
          <ValidatedDropdown
            label={
              <Box display="inline">
                {t('noteForm.contactDetails.contactType', 'Contact type')}
                <Tooltip
                  title={t(
                    'noteForm.contactDetails.contactTypeTooltip',
                    'Please make sure to only use BJB approved communication channels as per JBG-2048-00. The JB-approved channel BJB Chat includes WhatsApp Business, e-Banking and BOI, BJB Phone includes Desk phone, Mobile with BJB SIM, soft phone/Cisco Jabber and Webex. Chose “Other” to document any exceptional usage of non-JB approved communication channels.',
                  )}
                  placement="left-start"
                  arrow
                  id="contactType-tooltip"
                >
                  <InfoOutlinedIcon style={{ maxHeight: '12px', color: 'currentColor' }} />
                </Tooltip>
              </Box>
            }
            name="contactType"
            options={ContactTypeOptions}
            isMultiChoice={false}
            isMandatory
            isDisabled={isNoteFromEmail}
          />
        </Grid>
        <Grid item xs={6}>
          {values.contactType === ContactType.OTHER && (
            <ValidatedTextField
              isMandatory
              label={
                <Box display="inline">
                  {t('noteForm.contactDetails.contactTypeOther', 'Contact type - Other')}
                  <Tooltip
                    title={t(
                      'noteForm.contactDetails.contactTypeOtherTooltip',
                      'Please specify the communication channel, e.g. private phone, private e-mail, other video such as teams, or zoom, other digital channels and chats, text message/SMS.',
                    )}
                    placement="left-start"
                    arrow
                    id="contactTypeOther-tooltip"
                  >
                    <InfoOutlinedIcon style={{ maxHeight: '12px', color: 'currentColor' }} />
                  </Tooltip>
                </Box>
              }
              name="contactTypeOther"
              placeholder={t('noteForm.contactDetails.contactTypeOtherPlaceholder')}
            />
          )}
        </Grid>

        {!isSourceTravelTool && (
          <>
            {(isNoteFromEmail || contactTypesThatAllowDirection.includes(values.contactType as ContactType)) && (
              <Grid item xs={12} md={12} lg={6}>
                <FormLabel
                  required
                  component="legend"
                  error={
                    Object.prototype.hasOwnProperty.call(errors, 'direction') &&
                    Object.prototype.hasOwnProperty.call(touched, 'direction')
                  }
                >
                  {t('noteForm.contactDetails.direction', 'Direction')}
                </FormLabel>
                {/* <Typography display={'inline'}>
									{t('noteForm.contactDetails.direction', 'Direction')}
								</Typography>
								<MandatoryAsterisk /> */}

                <RadioGroup row>
                  <ValidatedRadio
                    name="direction"
                    id="radio-incoming-radio-button"
                    type="radio"
                    value={ContactDirection.INCOMING}
                    label={t('noteForm.contactDetails.incoming', 'Incoming')}
                  />
                  <ValidatedRadio
                    name="direction"
                    id="radio-outgoing-radio-button"
                    type="radio"
                    value={ContactDirection.OUTGOING}
                    label={t('noteForm.contactDetails.outgoing', 'Outgoing')}
                  />
                </RadioGroup>

                {Object.prototype.hasOwnProperty.call(errors, 'direction') &&
                  Object.prototype.hasOwnProperty.call(touched, 'direction') && (
                    <FormHelperText error>{(errors as any).direction}</FormHelperText>
                  )}
              </Grid>
            )}
          </>
        )}

        {isSourceTravelTool && (
          <Grid item xs={12} md={12} lg={6}>
            <ValidatedTextField
              disabled={true}
              label={t('noteForm.contactDetails.source', 'Source')}
              name="source"
              maxChar={200}
            />
          </Grid>
        )}

        {(isSourceTravelTool ||
          values.contactType === ContactType.EVENT ||
          values.contactType === ContactType.MEETING) && (
          <>
            <Grid item xs={12} md={12} lg={6}>
              <ValidatedTextField
                disabled={isSourceTravelTool}
                label={t('noteForm.contactDetails.tripId', 'Trip ID')}
                name="tripId"
                maxChar={200}
              />
            </Grid>
            <Grid item xs={0} md={0} lg={6} display={{ xs: 'none', md: 'none', lg: 'inline' }} />
          </>
        )}

        {['EMAIL', 'FAX', 'LETTER'].includes(values.contactType) && (
          <>
            <Grid item xs={12} md={12} lg={6}>
              <CustomDateTimePicker
                name="startDateTime"
                label={`${
                  isNoteFromEmail
                    ? t('noteForm.contactDetails.dateTime', 'Date & time')
                    : t('noteForm.contactDetails.submissionDateTime', 'Submission date & time')
                }`}
                isMandatory
                minDate={isSourceTravelTool ? undefined : 45}
                disabled={isNoteFromEmail}
              />
            </Grid>
            {!isNoteFromEmail && (
              <Grid item xs={12} md={12} lg={6}>
                <CustomDateTimePicker
                  name="endDateTime"
                  label={t('noteForm.contactDetails.receptionDateTime', 'Reception date & time')}
                  isMandatory
                  minDate={isSourceTravelTool ? undefined : 45}
                />
              </Grid>
            )}
            {isNoteFromEmail && <Grid item xs={0} md={0} lg={6} display={{ xs: 'none', md: 'none', lg: 'inline' }} />}
          </>
        )}
        {['MEETING', 'EVENT', 'OTHER', 'PHONE_CALL', 'CHAT', 'VIDEO_CONFERENCE', ''].includes(values.contactType) && (
          <>
            <Grid item xs={12} md={12} lg={6}>
              <CustomDateTimePicker
                name="startDateTime"
                label={t('noteForm.contactDetails.startDateTime', 'Meeting start date & time')}
                isMandatory
                minDate={isSourceTravelTool ? undefined : 45}
              />
            </Grid>
            <Grid item xs={12} md={12} lg={6}>
              <CustomDateTimePicker
                name="endDateTime"
                label={t('noteForm.contactDetails.endDateTime', 'Meeting end date & time')}
                isMandatory
                minDate={isSourceTravelTool ? undefined : 45}
              />
            </Grid>
          </>
        )}
        {(contactTypesThatAllowLocation.has(values.contactType) || String(values.crossBorderActivity) === 'true') && (
          <>
            <Grid item xs={12} md={12} lg={6}>
              <ValidatedDropdown
                label={t('noteForm.contactDetails.locationCountry', 'Country')}
                {...((String(values.crossBorderActivity) === 'true' || values.contactType === ContactType.MEETING) && {
                  isMandatory: true,
                })}
                name="locationCountry"
                options={countryOptions}
                isMultiChoice={false}
                isDisabled={isSourceTravelTool}
              />
            </Grid>
            <Grid item xs={12} md={12} lg={6}>
              <ValidatedTextField
                label={t('noteForm.contactDetails.locationCity', 'Location(City)')}
                name="locationCity"
                maxChar={50}
              />
            </Grid>
          </>
        )}
        <Grid item xs={12} md={12} lg={6}>
          <FormLabel
            required
            component="legend"
            error={
              Object.prototype.hasOwnProperty.call(errors, 'qualifiedClientContact') &&
              Object.prototype.hasOwnProperty.call(touched, 'qualifiedClientContact')
            }
          >
            {t('noteForm.contactDetails.qualifiedContact', 'Qualified client contact')}
            <Tooltip
              title={t(
                'noteForm.contactDetails.qualifiedClientTooltip',
                'A “qualified contact” is any communication between a prospect, an account holder and/or an authorized signatory, power of attorney, heir (i.e. person with decision power) and BJB which is i) initiated by a person with decision power or ii) initiated by BJB leading to a reaction by a person with decision power.',
              )}
              placement="left-start"
              arrow
              id="qualified-contact-tooltip"
            >
              <InfoOutlinedIcon style={{ maxHeight: '12px', color: 'currentColor' }} />
            </Tooltip>
          </FormLabel>

          <RadioGroup row>
            <ValidatedRadio
              name="qualifiedClientContact"
              id="qualifiedClientContact-radio-yes-radio-button"
              type="radio"
              value="true"
              label={t('noteForm.contactDetails.yes', 'Yes')}
            />
            <ValidatedRadio
              name="qualifiedClientContact"
              id="qualifiedClientContact-radio-no-radio-button"
              type="radio"
              value="false"
              label={t('noteForm.contactDetails.no', 'No')}
            />
          </RadioGroup>

          {Object.prototype.hasOwnProperty.call(errors, 'qualifiedClientContact') &&
            Object.prototype.hasOwnProperty.call(touched, 'qualifiedClientContact') && (
              <FormHelperText error>{errors.qualifiedClientContact}</FormHelperText>
            )}
        </Grid>
        <Grid item xs={12} md={12} lg={6}>
          <FormLabel
            required
            component="legend"
            error={
              Object.prototype.hasOwnProperty.call(errors, 'crossBorderActivity') &&
              Object.prototype.hasOwnProperty.call(touched, 'crossBorderActivity')
            }
          >
            {t('noteForm.contactDetails.crossBorderActivity', 'Cross border activity')}
            <Tooltip
              title={t(
                'noteForm.contactDetails.crossBorderActivityTooltip',
                'A “qualified contact” is any communication between a prospect, an account holder and/or an authorized signatory, power of attorney, heir (i.e. person with decision power) and BJB which is i) initiated by a person with decision power or ii) initiated by BJB leading to a reaction by a person with decision power.',
              )}
              placement="left-start"
              arrow
              id="cross-border-activity-tooltip"
            >
              <InfoOutlinedIcon style={{ maxHeight: '12px', color: 'currentColor' }} />
            </Tooltip>
          </FormLabel>
          <RadioGroup row>
            <ValidatedRadio
              name="crossBorderActivity"
              id="crossBorderActivity-radio-yes-radio-button"
              type="radio"
              value="true"
              label={t('noteForm.contactDetails.yes', 'Yes')}
            />
            <ValidatedRadio
              name="crossBorderActivity"
              id="crossBorderActivity-radio-no-radio-button"
              type="radio"
              value="false"
              label={t('noteForm.contactDetails.no', 'No')}
            />
          </RadioGroup>

          {Object.prototype.hasOwnProperty.call(errors, 'crossBorderActivity') &&
            Object.prototype.hasOwnProperty.call(touched, 'crossBorderActivity') && (
              <FormHelperText error>{errors.crossBorderActivity}</FormHelperText>
            )}
        </Grid>
        <Grid item xs={12} md={12} lg={6}>
          <ValidatedDropdown
            label={t('noteForm.contactDetails.topics', 'Topic(s)')}
            name="topics"
            isMandatory
            options={topicOptions}
            isMultiChoice={true}
            groupBy={(option) => option.category}
            isDisabled={isSourceTravelTool}
          />
        </Grid>
        {/*{isNoteFromEmail &&*/}
        {/*    <Grid item md={12} lg={6}>*/}
        {/*        <ValidatedDropdown label="Involved role(s)" name="involvedRoles" options={topicOptions}*/}
        {/*                        isMultiChoice={true} isDisabled={false}/>*/}
        {/*    </Grid>*/}
        {/*}*/}
        <Grid item xs={12} md={12} lg={6}>
          <ValidatedDropdown
            label={
              <Box display="inline">
                Initiated by
                <Tooltip
                  title={t(
                    'noteForm.contactDetails.complianceAlert',
                    'Ensure compliance with respective country manual',
                  )}
                  placement="left-start"
                  arrow
                  id="initiated-by-tooltip"
                >
                  <InfoOutlinedIcon style={{ maxHeight: '12px', color: 'currentColor' }} />
                </Tooltip>
              </Box>
            }
            name="initiatedBy"
            id="initiatedBy-content-input"
            options={initiatedByOptions}
            isMultiChoice={false}
            isMandatory
            isDisabled={false}
          />
        </Grid>
        {isNoteFromEmail && (
          <Grid item xs={12}>
            <ValidatedTextField
              label={t('noteForm.contactDetails.emailContent', 'Email content')}
              disabled
              name="body"
              maxChar={10_000}
              rowCount={isExpanded ? 10 : 5}
            />
            <Typography variant="caption">{isExpanded ? 'Hide details' : 'Show details'}</Typography>
            <IconButton onClick={handleExpandClick} aria-expanded={isExpanded} aria-label="show details" size="large">
              {isExpanded ? (
                <ExpandMoreIcon style={{ color: JBColors.support.greenSmoke[80] }} />
              ) : (
                <ExpandLessIcon style={{ color: JBColors.support.greenSmoke[80] }} />
              )}
            </IconButton>
          </Grid>
        )}
        <Grid item xs={12}>
          <ValidatedTextField
            customId="notes-text-input"
            label={t('noteForm.contactDetails.notes', 'Notes')}
            name="notes"
            placeholder={t('noteForm.contactDetails.notesPlaceholder')}
            maxChar={3900}
            rowCount={8}
            isMandatory
            showCharacterCount
          />
          <JBAlert color="info" collapsible>
            <AlertTitle sx={{ fontWeight: 'bold' }}>
              {t('noteForm.contactDetails.Specifications', 'Specifications')}
            </AlertTitle>
            <Trans
              i18nKey="noteForm.contactDetails.noteRequirements"
              t={t}
              components={[
                <Link href={DOCUMENT_JBG_2038_00} key={DOCUMENT_JBG_2038_00} />,
                <Link href={DOCUMENT_COUNTRY_GUIDELINES} key={DOCUMENT_COUNTRY_GUIDELINES} />,
                <Link href={DOCUMENT_JBG_2048_00} key={DOCUMENT_JBG_2048_00} />,
              ]}
            />
          </JBAlert>
        </Grid>
        <Grid item xs={12}>
          <Divider textAlign="left">{t('documents.title', 'Documents')} </Divider>
        </Grid>
        <Grid item xs={12}>
          <DocumentDropZone />
        </Grid>
        <Grid item xs={12} md={12} lg={6} />
      </Grid>
    </JBSection>
  );
};

export default ContactnoteForm;
